import { applyPatch, type Operation } from "fast-json-patch";
import type { DashboardSpec, Followup, JsonPatchOp, Section } from "../types";

// Default starter chips shown in the empty state — one per locked taxonomy
// slot (drill / compare / pivot / verify). Hard-coded by design: keeps first
// paint at zero edge-fn cost and the chip rail visually balanced.
export const SEED_CHIPS: Followup[] = [
  { text: "Give me an overview of the portfolio", kind: "drill" },
  { text: "Compare stores by survey completion", kind: "compare" },
  { text: "Show me incomplete nameplate data", kind: "pivot" },
  { text: "Which stores are missing photos?", kind: "verify" },
];

// Synthetic accordion value for the in-flight (streaming) panel. Held in the
// expanded set permanently so the live answer is always open while it streams;
// Radix ignores the value when no item with it is mounted.
export const LIVE_PANEL_ID = "__live__";

export function newId(): string {
  return crypto.randomUUID();
}

/** Normalize a question for the pre-flight short-circuit equality check (D19). */
export function normalizeQuestion(q: string): string {
  return q
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Dominant artifact type of a spec: first non-`answer` section kind, else "answer". */
export function dominantType(spec: DashboardSpec): string {
  const nonAnswer = spec.sections.find((s) => s.kind !== "answer");
  return nonAnswer ? nonAnswer.kind : "answer";
}

/** Per-section data-bearing fields for hashing (codex #6). Pulls the fields that
 *  carry the underlying numbers/text, NOT cosmetics (chart_type, colors, titles).
 *  The generic `data ?? rows ?? items ?? cells` fallback missed bullet
 *  (value/target/bands) and answer/markdown/callout bodies, so materially
 *  different artifacts could hash identically and falsely de-dup. */
function sectionDataPayload(section: Section): unknown {
  const s = section as Section & Record<string, unknown>;
  switch (section.kind) {
    case "answer":
    case "markdown":
      return { body: s.body };
    case "callout":
      return { severity: s.severity, body: s.body };
    case "kpi_row":
      return { items: s.items };
    case "table":
    case "stat_table":
      return { columns: s.columns, rows: s.rows };
    case "chart":
      // chart_type excluded on purpose — swapping bar↔pie is cosmetic (L23).
      return { x_key: s.x_key, series: s.series, data: s.data };
    case "list":
    case "bar_list":
      return { items: s.items };
    case "bullet":
      return { value: s.value, target: s.target, bands: s.bands };
    case "sparkline":
      return { x_key: s.x_key, y_key: s.y_key, data: s.data };
    case "tracker":
      return { cells: s.cells };
    case "tab_group":
      return { tabs: s.tabs };
    default:
      return { data: s.data ?? s.rows ?? s.items ?? s.cells ?? null };
  }
}

/** Deterministic data payload for hashing — data-bearing fields only, no cosmetics. */
export function artifactDataPayload(spec: DashboardSpec): unknown {
  return spec.sections.map((section) => ({
    kind: section.kind,
    data: sectionDataPayload(section),
  }));
}

/** Decode value_json on each op and apply RFC-6902 patch to a cloned spec. */
export function applyPatchOps(spec: DashboardSpec, ops: JsonPatchOp[]): DashboardSpec {
  const decoded: Operation[] = ops.map((o) => ({
    op: o.op,
    path: o.path,
    from: o.from,
    value: o.value_json !== undefined ? JSON.parse(o.value_json) : undefined,
  })) as Operation[];
  const next = applyPatch(structuredClone(spec), decoded).newDocument as DashboardSpec;
  // A structurally-valid op can remove/replace /sections and leave it a
  // non-array without throwing — that would crash the renderer. Treat it as a
  // failed patch so the caller's catch shows "couldn't apply" and keeps the
  // prior spec.
  if (!Array.isArray(next.sections)) {
    throw new Error("Patch produced a dashboard with no sections array");
  }
  return next;
}
